<template>
  <div class="button-div">
    <div class="div-checkbox-button-poi">
      <ListMenuWithCheckbox
        v-if="
          (clickedItemType === 'realEstate' ||
            clickedItemType === 'poi' ||
            clickedItemType === 'line' ||
            clickedItemType === 'zone') &&
          (showPoiMenu || showPoiMenuHover)
        "
        :categories="categories"
        :title="$t('general__view__pois')"
        @onChecklistToggle="onChecklistToggle"
        @onHoverListMenu="$emit('onHoverListMenu')"
        @closeHoverListMenu="$emit('closeHoverListMenu')"
      />
      <ButtonSquareHoverChecklist
        :title="$t('general__view__pois')"
        :imgSource="iconPoi"
        size="large"
        style="padding-top: 24px"
        :activeState="poiButtonActiveState"
        @clicked="toggleVisible"
        @hovered="$emit('MapPoiButtonHovered')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useStore } from 'vuex'
import ButtonSquareHoverChecklist from '@/components/general/buttons/ButtonSquareHoverChecklist.vue'
import ListMenuWithCheckbox from '@/components/general/lists/ListMenuWithCheckbox/ListMenuWithCheckbox.vue'
import iconPoi from '@/assets/icons-general/icon-poi.svg'
import { MAPVIEW_PREFIX } from '@/store/module-prefixes'
import { SET_POI_CATEGORIES_FOR_SELECTED_REAL_ESTATE, SET_POI_MENU } from '@/store/mutation-types'

// Props and emits
const props = defineProps<{
  categories: Array<any>
  showPoiMenuHover: boolean
  title: string
}>()
const emit = defineEmits([
  'MapPoiButtonHovered',
  'onChecklistToggle',
  'onHoverListMenu',
  'closeHoverListMenu'
])

// Vuex store
const store = useStore()

// State
const clickedItemType = computed(() => store.state.clickedItemType)
const showPoiMenu = computed(() => store.state.showPoiMenu)
const outdoorPoisForSelectedRealEstate = computed(
  () => store.state[MAPVIEW_PREFIX].outdoorPoisForSelectedRealEstate
)
const poiCategoriesForSelectedRealEstate = computed(
  () => store.state[MAPVIEW_PREFIX].poiCategoriesForSelectedRealEstate
)

// Compute active state for the button
const poiButtonActiveState = computed(() => {
  if (!props.categories.length) {
    setPoiMenu(false)
    return 'deactivated'
  }
  if (showPoiMenu.value) {
    return 'active'
  } else {
    return 'inactive'
  }
})

// Watch for changes in outdoorPoisForSelectedRealEstate to update categories
watch(outdoorPoisForSelectedRealEstate, (newPoisArray) => {
  if (newPoisArray) {
    const updatedCategories = [...poiCategoriesForSelectedRealEstate.value]
    poiCategoriesForSelectedRealEstate.value?.forEach((poiCategory, categoryIndex) => {
      poiCategory?.types.forEach((poiType, typeIndex) => {
        const poiExistsInType = newPoisArray.find((poi) => poi.properties.typeId === poiType.id)
        // Removing empty type and/or category after poi category is edited
        if (!poiExistsInType && poiType.visible) {
          if (poiCategory.types.length > 1) {
            // Remove the type only from store
            updatedCategories[categoryIndex].types.splice(typeIndex, 1)
          } else if (poiCategory.types.length === 1) {
            // Remove the full category from store
            updatedCategories.splice(categoryIndex, 1)
          }
          setPoiCategoriesForSelectedRealEstate({
            categories: updatedCategories
          })
        }
      })
    })
  }
})

// Mutations
function setPoiMenu(value: boolean) {
  store.commit(SET_POI_MENU, value)
}
function setPoiCategoriesForSelectedRealEstate(payload: { categories: any[] }) {
  store.commit(`${MAPVIEW_PREFIX}/${SET_POI_CATEGORIES_FOR_SELECTED_REAL_ESTATE}`, payload)
}

// Toggle visibility of POI menu
function toggleVisible() {
  if (!props.categories.length) return
  setPoiMenu(!showPoiMenu.value)
}

// Checklist toggle handler
function onChecklistToggle(typeId: string, categoryId: string, checked: boolean) {
  emit('onChecklistToggle', typeId, categoryId, checked)
}
function hideHoverButtons() {
  setPoiMenu(false)
}

defineExpose({
  hideHoverButtons
})
</script>

<style lang="scss" scoped>
.div-checkbox-button-poi {
  display: flex;
  justify-content: center;
}
</style>
